import React, { useEffect, useState } from "react";
import "./App.css";

// ════════════════════════════════════════════════════════════════
//  Odds Supremacy Tab
// ════════════════════════════════════════════════════════════════

function OddsTab() {
  const ODDS_URL = "/odds-api";

  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState("");
  const [fixtures, setFixtures] = useState([]);
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [openingData, setOpeningData] = useState(null);
  const [openingLoading, setOpeningLoading] = useState(false);
  const [openingError, setOpeningError] = useState("");
  const [closingData, setClosingData] = useState(null);
  const [closingLoading, setClosingLoading] = useState(false);
  const [closingError, setClosingError] = useState("");
  const [fixturesLoading, setFixturesLoading] = useState(false);
  const [snapshotsLoading, setSnapshotsLoading] = useState(false);
  const [error, setError] = useState("");
  const [health, setHealth] = useState(null);
  const [healthError, setHealthError] = useState("");
  const [refreshVersion, setRefreshVersion] = useState(0);
  const loading = selectedFixture ? snapshotsLoading : fixturesLoading;
  const supDescription = "Full-match Basis supremacy, matching the yellow Sup input in your Poisson sheet. Positive favours home; negative favours away.";
  const tgDescription = "Full-match Basis goals, matching the yellow Goals input in your Poisson sheet. This is a scoring rate over a full match, not goals still to come or a final-score prediction.";

  const styles = {
    page: {
      maxWidth: "1450px",
      fontFamily: "Arial, Helvetica, sans-serif",
    },
    controls: {
      display: "flex",
      gap: "16px",
      alignItems: "end",
      flexWrap: "wrap",
      marginBottom: "18px",
    },
    label: {
      display: "block",
      fontSize: "13px",
      fontWeight: 700,
      marginBottom: "5px",
    },
    select: {
      padding: "8px 10px",
      border: "1px solid #cbd5e1",
      borderRadius: "6px",
      minWidth: "260px",
      background: "#fff",
    },
    tableWrap: {
      overflowX: "auto",
      marginTop: "10px",
    },
    table: {
      borderCollapse: "collapse",
      fontSize: "13px",
      background: "#fff",
      minWidth: "520px",
    },
    th: {
      border: "1px solid #cbd5e1",
      background: "#eaf0f7",
      padding: "7px 9px",
      textAlign: "left",
      fontWeight: 800,
      whiteSpace: "nowrap",
    },
    thNum: {
      border: "1px solid #cbd5e1",
      background: "#eaf0f7",
      padding: "7px 9px",
      textAlign: "right",
      fontWeight: 800,
      whiteSpace: "nowrap",
    },
    td: {
      border: "1px solid #d6dde7",
      padding: "7px 9px",
      verticalAlign: "middle",
      whiteSpace: "nowrap",
    },
    num: {
      border: "1px solid #d6dde7",
      padding: "7px 9px",
      textAlign: "right",
      fontFamily: "Menlo, Monaco, Consolas, monospace",
      whiteSpace: "nowrap",
    },
    button: {
      background: "#2563eb",
      color: "#fff",
      border: 0,
      borderRadius: "6px",
      padding: "7px 11px",
      fontWeight: 700,
      cursor: "pointer",
    },
    backButton: {
      background: "#e5e7eb",
      color: "#111827",
      border: 0,
      borderRadius: "6px",
      padding: "8px 12px",
      fontWeight: 700,
      cursor: "pointer",
      marginBottom: "14px",
    },
    card: {
      border: "1px solid #cbd5e1",
      borderRadius: "12px",
      padding: "18px",
      background: "#ffffff",
      boxShadow: "0 1px 4px rgba(15,23,42,0.15)",
      maxWidth: "980px",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      gap: "18px",
      flexWrap: "wrap",
      alignItems: "flex-start",
      borderBottom: "2px solid #e5e7eb",
      paddingBottom: "14px",
      marginBottom: "18px",
    },
    title: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "10px",
      fontSize: "24px",
      fontWeight: 900,
    },
    versus: {
      color: "#64748b",
      fontSize: "20px",
      fontWeight: 800,
    },
    subtitle: {
      marginTop: "6px",
      fontSize: "14px",
      color: "#475569",
    },
    note: {
      fontSize: "12px",
      color: "#64748b",
      maxWidth: "310px",
      lineHeight: 1.35,
    },
    section: {
      marginTop: "22px",
    },
    sectionTitle: {
      fontSize: "18px",
      fontWeight: 900,
      margin: "0 0 9px 0",
    },
    muted: {
      color: "#64748b",
      fontSize: "13px",
    },
    error: {
      color: "#b91c1c",
      fontWeight: 700,
      fontSize: "13px",
    },
    pillPre: {
      display: "inline-block",
      background: "#dbeafe",
      color: "#1e40af",
      borderRadius: "999px",
      padding: "3px 8px",
      fontSize: "11px",
      fontWeight: 800,
    },
    pillLive: {
      display: "inline-block",
      background: "#dcfce7",
      color: "#166534",
      borderRadius: "999px",
      padding: "3px 8px",
      fontSize: "11px",
      fontWeight: 800,
    },
    blank: {
      color: "#94a3b8",
    },
  };

  const preTargets = [
    { label: "4 days", minutes: 5760, tolerance: 5 },
    { label: "3 days", minutes: 4320, tolerance: 5 },
    { label: "2 days", minutes: 2880, tolerance: 5 },
    { label: "24 hr", minutes: 1440, tolerance: 5 },
    { label: "12 hr", minutes: 720, tolerance: 5 },
    { label: "6 hr", minutes: 360, tolerance: 5 },
    { label: "3 hr", minutes: 180, tolerance: 5 },
    { label: "1hr 15m", minutes: 75, tolerance: 5 },
    { label: "1 hr", minutes: 60, tolerance: 5 },
    { label: "30 min", minutes: 30, tolerance: 5 },
  ];

  const inPlayTargets = [
    { label: "0", closing: true },
    ...Array.from({ length: 22 }, (_, i) => ({ label: String(2 + i * 2), minutes: 2 + i * 2, half: 1 })),
    { label: "HT", status: "half" },
    ...Array.from({ length: 23 }, (_, i) => ({ label: String(46 + i * 2), minutes: 46 + i * 2, half: 2 })),
    { label: "FT", status: "finished" },
  ];

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") setRefreshVersion((value) => value + 1);
    };
    const timer = window.setInterval(refresh, 60000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${ODDS_URL}/leagues`, { signal: controller.signal, cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error("Could not load leagues");
        return r.json();
      })
      .then((d) => {
        if (controller.signal.aborted) return;
        const list = Array.isArray(d.leagues) ? d.leagues : [];
        setLeagues(list);
        setSelectedLeague((current) => current || list[0] || "");
      })
      .catch((err) => {
        if (err.name !== "AbortError") setError("Could not load leagues");
      });
    return () => controller.abort();
  }, [refreshVersion]);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${ODDS_URL}/health`, { signal: controller.signal, cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error("Health check failed");
        return r.json();
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        setHealth(data);
        setHealthError("");
      })
      .catch((err) => {
        if (err.name !== "AbortError") setHealthError("The latest odds collection status could not be checked.");
      });
    return () => controller.abort();
  }, [refreshVersion]);

  useEffect(() => {
    if (!selectedLeague) return;
    const controller = new AbortController();
    setFixturesLoading(true);
    setError("");
    fetch(`${ODDS_URL}/summary?league=${encodeURIComponent(selectedLeague)}`, { signal: controller.signal, cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error("Failed to load fixtures");
        return r.json();
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        const safe = Array.isArray(data) ? data : [];
        safe.sort((a, b) => new Date(b.kickoff_utc) - new Date(a.kickoff_utc));
        setFixtures(safe);
      })
      .catch((err) => {
        if (err.name !== "AbortError") setError("Failed to load fixtures. Previously loaded rows may be out of date.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setFixturesLoading(false);
      });
    return () => controller.abort();
  }, [selectedLeague, refreshVersion]);

  useEffect(() => {
    if (!selectedFixture) return;
    const controller = new AbortController();
    setSnapshotsLoading(true);
    setError("");
    fetch(`${ODDS_URL}/snapshots?fixture_id=${encodeURIComponent(selectedFixture)}&limit=2000`, { signal: controller.signal, cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error("Failed to load snapshots");
        return r.json();
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        const safe = Array.isArray(data) ? data : [];
        safe.sort((a, b) => new Date(a.snapshot_utc) - new Date(b.snapshot_utc));
        setSnapshots(safe);
      })
      .catch((err) => {
        if (err.name !== "AbortError") setError("Failed to load snapshots. Previously loaded rows may be out of date.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setSnapshotsLoading(false);
      });
    return () => controller.abort();
  }, [selectedFixture, refreshVersion]);

  useEffect(() => {
    if (!selectedFixture) return;
    const controller = new AbortController();
    setClosingLoading(true);
    setClosingError("");
    fetch(`${ODDS_URL}/closing?fixture_id=${encodeURIComponent(selectedFixture)}`, { signal: controller.signal, cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error("Closing archive check failed");
        return r.json();
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        if (!data || String(data.fixture_id) !== String(selectedFixture)) throw new Error("Closing fixture mismatch");
        setClosingData(data);
      })
      .catch((err) => {
        if (!controller.signal.aborted && err.name !== "AbortError") setClosingError("The closing-price archive could not be checked.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setClosingLoading(false);
      });
    return () => controller.abort();
  }, [selectedFixture, refreshVersion]);

  useEffect(() => {
    if (!selectedFixture) return;
    const controller = new AbortController();
    setOpeningLoading(true);
    setOpeningError("");
    fetch(`${ODDS_URL}/opening?fixture_id=${encodeURIComponent(selectedFixture)}`, { signal: controller.signal, cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error("Opening prices check failed");
        return r.json();
      })
      .then((data) => {
        if (controller.signal.aborted) return;
        if (!data || String(data.fixture_id) !== String(selectedFixture)) throw new Error("Opening fixture mismatch");
        setOpeningData(data);
      })
      .catch((err) => {
        if (!controller.signal.aborted && err.name !== "AbortError") setOpeningError("The provider’s opening prices could not be checked.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setOpeningLoading(false);
      });
    return () => controller.abort();
  }, [selectedFixture, refreshVersion]);

  const isPresent = (value) => value !== undefined && value !== null && (typeof value !== "string" || value.trim() !== "");

  const formatDate = (s) => {
    if (!s) return "";
    try {
      const d = new Date(s);
      return d.toLocaleString("en-GB", {
        timeZone: "Europe/London",
        weekday: "short",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return s;
    }
  };

  const formatDateLong = (s) => {
    if (!s) return "";
    try {
      const d = new Date(s);
      return d.toLocaleString("en-GB", {
        timeZone: "Europe/London",
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return s;
    }
  };

  const formatProviderTime = (value) => {
    if (!isPresent(value) || !Number.isFinite(Date.parse(value))) return "";
    return new Date(value).toLocaleString("en-GB", {
      timeZone: "Europe/London", weekday: "short", day: "2-digit", month: "short",
      year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  };

  const formatSup = (value) => {
    if (!isPresent(value)) return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    const rounded = n.toFixed(2);
    return rounded === "-0.00" ? "0.00" : rounded;
  };

  const formatTg = (value) => {
    if (!isPresent(value)) return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return n.toFixed(2);
  };

  const compactSnapshots = (() => {
    const grouped = new Map();

    snapshots.forEach((row, index) => {
      const key = [
        row.snapshot_utc || "",
        row.phase || "",
        row.bookmaker || "",
        isPresent(row.offset_minutes) ? row.offset_minutes : "",
        isPresent(row.clock_minute) ? row.clock_minute : "",
        row.clock_period || "",
        row.clock_status || "",
      ].join("||");

      if (!grouped.has(key)) {
        grouped.set(key, {
          ...row,
          _rows: [],
          _firstIndex: index,
        });
      }

      const existing = grouped.get(key);
      existing._rows.push(row);

      if (!isPresent(existing.sup) && isPresent(row.sup)) existing.sup = row.sup;
      if (!isPresent(existing.tg) && isPresent(row.tg)) existing.tg = row.tg;
      ["home_score", "away_score", "home_red_cards", "away_red_cards", "state_source", "calculation_status"].forEach((field) => {
        if (!isPresent(existing[field]) && isPresent(row[field])) existing[field] = row[field];
      });
    });

    return Array.from(grouped.values()).sort((a, b) => {
      const ad = new Date(a.snapshot_utc || 0).getTime();
      const bd = new Date(b.snapshot_utc || 0).getTime();
      return ad - bd || (a._firstIndex || 0) - (b._firstIndex || 0);
    });
  })();

  const numberOrNull = (value) => {
    if (!isPresent(value)) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const clockMinute = (row) => {
    const minute = numberOrNull(row?.clock_minute);
    return minute !== null && minute >= 0 ? minute : null;
  };

  const matchStage = (row) => {
    const token = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const status = token(row?.clock_status);
    const period = token(row?.clock_period);
    const phase = token(row?.phase);
    if ([status, period].some((value) => ["ft", "fulltime", "finished", "ended", "aet", "afterextratime", "ftpen", "afterpenalties"].includes(value))) return "finished";
    if ([status, period].some((value) => ["ht", "halftime", "half", "halftimebreak"].includes(value))) return "half";
    if (phase.includes("pre") || ["ns", "notstarted", "scheduled", "prematch"].includes(status)) return "pre";
    if (phase.includes("inplay") || phase.includes("live") || status.includes("inplay") || status === "live" || clockMinute(row) !== null) return "live";
    return "unknown";
  };

  const clockHalf = (row) => {
    const token = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const period = token(row?.clock_period);
    const status = token(row?.clock_status);
    if (["3", "4", "5", "et", "extratime", "penalties"].includes(period) || status.includes("extratime") || status.includes("penalties")) return "unsupported";
    if (["1", "1h", "first", "firsthalf", "1sthalf"].includes(period) || status.includes("1sthalf") || status.includes("firsthalf")) return 1;
    if (["2", "2h", "second", "secondhalf", "2ndhalf"].includes(period) || status.includes("2ndhalf") || status.includes("secondhalf")) return 2;
    return null;
  };

  const preMatchAllRows = compactSnapshots.filter((row) => matchStage(row) === "pre");
  const inPlayAllRows = compactSnapshots.filter((row) => matchStage(row) !== "pre");
  const unknownClockRows = inPlayAllRows.filter((row) => clockMinute(row) === null && !["half", "finished"].includes(matchStage(row)));

  const rowOffsetMinutes = (row) => {
    const kickoffTime = Date.parse(row?.kickoff_utc);
    const capturedTime = Date.parse(row?.snapshot_utc);
    return Number.isFinite(kickoffTime) && Number.isFinite(capturedTime)
      ? (kickoffTime - capturedTime) / 60000 : null;
  };

  const formatActualPre = (row) => {
    const mins = rowOffsetMinutes(row);
    if (mins === null) return "";
    if (mins < 0) return `${Math.ceil(-mins)}m after scheduled KO`;
    const rounded = Math.round(mins);
    if (rounded >= 60) {
      const h = Math.floor(rounded / 60);
      const m = rounded % 60;
      return m ? `${h}h ${m}m` : `${h}h`;
    }

    return `${rounded}m`;
  };

  const nearestRow = (rows, target, usedIndexes, mode) => {
    if (target.status) {
      const candidates = rows
        .map((row, index) => ({ row, index }))
        .filter((x) => matchStage(x.row) === target.status && !usedIndexes.has(x.index))
        .sort((a, b) => new Date(b.row.snapshot_utc) - new Date(a.row.snapshot_utc));
      if (candidates.length === 0) return null;
      usedIndexes.add(candidates[0].index);
      return candidates[0].row;
    }

    const candidates = rows
      .map((row, index) => {
        const mins = mode === "pre" ? rowOffsetMinutes(row) : clockMinute(row);
        return {
          row,
          index,
          mins,
          diff: mins === null ? 999999 : Math.abs(mins - target.minutes),
        };
      })
      .filter((x) => {
        if (x.mins === null || usedIndexes.has(x.index) || x.diff > target.tolerance) return false;
        if (mode === "pre") return x.mins >= 0;
        if (matchStage(x.row) !== "live") return false;
        const half = clockHalf(x.row);
        return half === null || half === (target.minutes <= 45 ? 1 : 2);
      })
      .sort((a, b) => a.diff - b.diff || (mode === "pre" ? new Date(b.row.snapshot_utc) - new Date(a.row.snapshot_utc) : 0));

    if (candidates.length === 0) return null;

    usedIndexes.add(candidates[0].index);
    return candidates[0].row;
  };

  const preUsed = new Set();
  const observedInPlayTimes = inPlayAllRows
    .filter((row) => ["live", "half", "finished"].includes(matchStage(row)))
    .map((row) => Date.parse(row.snapshot_utc)).filter(Number.isFinite);
  const firstInPlayTime = observedInPlayTimes.length ? Math.min(...observedInPlayTimes) : null;
  const currentFixture = fixtures.find((row) => String(row.fixture_id) === String(selectedFixture));
  const confirmedStarted = firstInPlayTime !== null || (
    ["live", "halftime", "finished"].includes(currentFixture?.fixture_status)
    && ["sportmonks_fixture_cache", "sportmonks_sqlite"].includes(currentFixture?.fixture_status_source)
    && Number.isFinite(Date.parse(currentFixture?.fixture_status_observed_at))
  );
  const startedByTime = firstInPlayTime ?? Date.parse(currentFixture?.fixture_status_observed_at);
  const lastPreMatchRow = confirmedStarted ? [...preMatchAllRows]
    .filter((row) => Number.isFinite(Date.parse(row.snapshot_utc))
      && Date.parse(row.snapshot_utc) < startedByTime)
    .sort((a, b) => new Date(b.snapshot_utc) - new Date(a.snapshot_utc))[0] || null : null;
  const fixtureClosingData = closingData && String(closingData.fixture_id) === String(selectedFixture) ? closingData : null;
  const archiveRecords = fixtureClosingData?.status === "available" && Array.isArray(fixtureClosingData.records)
    ? fixtureClosingData.records.filter((row) => String(row.fixture_id) === String(selectedFixture)
      && row.source === "odds_api_historical" && row.kind === "provider_closing") : [];
  const archiveRecord = archiveRecords.find((row) => String(row.bookmaker).toLowerCase() === "singbet") || archiveRecords[0] || null;
  const closingRow = archiveRecord ? { ...archiveRecord, _providerClosing: true } : null;
  const closingStatusText = closingLoading ? "Checking archive…" : closingError ? "Archive check failed"
    : fixtureClosingData?.status === "unavailable" ? "Archive unavailable" : "Awaiting closing archive";
  const fixtureOpeningData = openingData && String(openingData.fixture_id) === String(selectedFixture) ? openingData : null;
  const openingRecords = fixtureOpeningData?.status === "available" && Array.isArray(fixtureOpeningData.records)
    ? fixtureOpeningData.records.filter((row) => String(row.fixture_id) === String(selectedFixture)
      && row.source === "odds_api_movements" && row.kind === "provider_opening") : [];
  const openingRecord = openingRecords.find((row) => String(row.bookmaker).toLowerCase() === "singbet") || openingRecords[0] || null;
  const openingRow = openingRecord ? { ...openingRecord, _providerOpening: true } : null;
  const openingUnavailableReason = {
    missing_spread_opening: "Handicap opening unavailable", missing_totals_opening: "Goals opening unavailable",
    invalid_spread_opening: "Handicap opening unverified", invalid_totals_opening: "Goals opening unverified",
    fixture_id_mismatch: "Opening could not be verified", bookmaker_mismatch: "Opening could not be verified",
    no_valid_opening_pairs: "Opening prices could not be verified",
  }[fixtureOpeningData?.reason] || "Opening unavailable";
  const openingStatusText = openingLoading ? "Checking opening prices…" : openingError ? "Opening check failed"
    : fixtureOpeningData?.status === "unavailable" ? openingUnavailableReason : "Awaiting provider opening";
  const preMatchRows = [{ target: { label: "Opening", opening: true }, row: openingRow }].concat(preTargets.map((target) => ({
    target,
    row: nearestRow(preMatchAllRows, target, preUsed, "pre"),
  })), { target: { label: "Closing", closing: true }, row: closingRow });
  if (!closingRow && lastPreMatchRow) {
    preMatchRows.push({ target: { label: "Last pre-match (unconfirmed)", provisional: true }, row: lastPreMatchRow });
  }

  const liveUsed = new Set();
  const visibleMinuteKey = (row) => `${clockHalf(row)}|${Number(clockMinute(row)?.toFixed(1))}`;
  const displayedMinutes = new Set();
  const addedTargets = (half, firstAnchor, boundary) => {
    const minutes = inPlayAllRows.filter((row) => matchStage(row) === "live" && clockHalf(row) === half)
      .map(clockMinute).filter((minute) => minute !== null && minute >= firstAnchor - 1 && minute <= 180);
    if (!minutes.length) return [];
    const lastAnchor = 2 * Math.floor((Math.max(...minutes) + 1) / 2);
    return Array.from({ length: (lastAnchor - firstAnchor) / 2 + 1 }, (_, i) => {
      const minute = firstAnchor + i * 2;
      return { label: `${boundary}+${minute - boundary}`, minutes: minute, half, addedTime: true, key: `added-${half}-${minute}` };
    });
  };
  const expandedInPlayTargets = inPlayTargets.flatMap((target) => target.status === "half"
    ? [...addedTargets(1, 46, 45), target] : target.status === "finished" ? [...addedTargets(2, 92, 90), target] : [target]);
  const inPlayRows = expandedInPlayTargets.map((target) => {
    if (target.closing) return { target, row: closingRow };
    if (target.status) return { target, row: nearestRow(inPlayAllRows, target, liveUsed, "live") };
    const candidates = inPlayAllRows.map((row, index) => ({ row, index, minute: clockMinute(row) }))
      .filter(({ row, index, minute }) => !liveUsed.has(index) && minute !== null
        && matchStage(row) === "live" && clockHalf(row) === target.half
        && !displayedMinutes.has(visibleMinuteKey(row))
        && minute >= target.minutes - 1 && minute < target.minutes + 1)
      .sort((a, b) => Math.abs(a.minute - target.minutes) - Math.abs(b.minute - target.minutes)
        || new Date(b.row.snapshot_utc) - new Date(a.row.snapshot_utc));
    if (!candidates.length) return { target, row: null };
    liveUsed.add(candidates[0].index);
    displayedMinutes.add(visibleMinuteKey(candidates[0].row));
    return { target, row: candidates[0].row };
  });
  const otherInPlayRows = inPlayAllRows.filter((row, index) => !liveUsed.has(index)
    && (clockMinute(row) !== null || ["half", "finished"].includes(matchStage(row))));

  const formatClock = (row) => {
    const stage = matchStage(row);
    const minute = clockMinute(row);
    const display = minute === null ? "" : Number(minute.toFixed(1));
    if (stage === "finished") return minute === null ? "FT" : `FT (${display})`;
    if (stage === "half") return minute === null ? "HT" : `HT (${display})`;
    if (clockHalf(row) === 1 && minute > 45) return `45+${Number((minute - 45).toFixed(1))}`;
    if (clockHalf(row) === 2 && minute > 90) return `90+${Number((minute - 90).toFixed(1))}`;
    return display;
  };

  const formatPair = (home, away) => {
    const h = numberOrNull(home);
    const a = numberOrNull(away);
    if (h === null && a === null) return "";
    return `${h === null ? "—" : h}–${a === null ? "—" : a}`;
  };

  const inPlayRowValues = (target, row) => ({
    time: row ? target.closing ? "0" : formatClock(row) : target.label,
    score: row && !target.closing ? formatPair(row.home_score, row.away_score) : "",
    reds: row && !target.closing ? formatPair(row.home_red_cards, row.away_red_cards) : "",
  });

  const lastOddsStage = (row) => ({
    pre: "Pre-match",
    live: "In-play",
    half: "Half-time",
    finished: "Full-time",
    unknown: "Unknown",
  }[matchStage(row)]);

  const fixtureStatusView = (row, now = Date.now()) => {
    const labels = {
      pending: "Not started", live: "In play", halftime: "Half-time", finished: "Finished",
      cancelled: "Cancelled", postponed: "Postponed", abandoned: "Abandoned", interrupted: "Interrupted",
    };
    const status = row?.fixture_status;
    const verified = ["sportmonks_fixture_cache", "sportmonks_sqlite"].includes(row?.fixture_status_source)
      && Number.isFinite(Date.parse(row?.fixture_status_observed_at))
      && Object.prototype.hasOwnProperty.call(labels, status);
    const kickoffTime = Date.parse(row?.kickoff_utc);
    return {
      status: verified ? status : "unknown",
      label: verified ? labels[status] : Number.isFinite(kickoffTime) && kickoffTime < now ? "Result unavailable" : "Status unavailable",
      score: verified ? formatPair(row.current_home_score, row.current_away_score) : "",
      scoreLabel: verified && status === "finished" ? "Final score" : "Latest score",
      observedAt: verified ? row.fixture_status_observed_at : "",
      scoreObservedAt: verified ? row.current_score_observed_at || row.fixture_status_observed_at : "",
    };
  };

  const fixtureStatusTitle = (row) => {
    const state = fixtureStatusView(row);
    return state.observedAt
      ? `Match status checked ${formatDateLong(state.observedAt)} (London time). This is separate from the saved odds.`
      : "No verified match status is available. The last saved odds do not establish whether the match has finished.";
  };

  const currentScoreTitle = (row) => {
    const state = fixtureStatusView(row);
    return state.score && state.scoreObservedAt
      ? `${state.scoreLabel}, checked ${formatDateLong(state.scoreObservedAt)} (London time). Home–away.`
      : "No verified match score is available. The score when the odds were saved is shown separately.";
  };

  const metric = (row, field) => {
    if (row?._providerClosing || row?._providerOpening) return field === "sup" ? formatSup(row.sup) : formatTg(row.tg);
    if (!row || matchStage(row) === "finished" || (matchStage(row) !== "pre" && clockMinute(row) === null)) return "";
    return field === "sup" ? formatSup(row.sup) : formatTg(row.tg);
  };

  const closingUpdateText = (row) => {
    const ah = formatProviderTime(row?.market_updated_at?.ah);
    const totals = formatProviderTime(row?.market_updated_at?.totals);
    if (ah && ah === totals) return `Markets updated ${ah}`;
    return `AH market updated: ${ah || "unavailable"}\nTG market updated: ${totals || "unavailable"}`;
  };

  const providerPricePairs = (row) => ["ah", "totals"].map((market) => {
      const prices = (Array.isArray(row?.quotes) ? row.quotes : []).filter((quote) => quote.market === market).map((quote) => {
        const side = String(quote.selection || "").split("_")[0];
        const label = {home: "Home", away: "Away", over: "Over", under: "Under"}[side] || side;
        const line = numberOrNull(quote.line);
        const price = numberOrNull(quote.price);
        const lineLabel = line === null ? "" : `${market === "ah" && line > 0 ? "+" : ""}${line}`;
        return price === null ? "" : `${label}${lineLabel ? ` ${lineLabel}` : ""} @ ${price.toFixed(2)}`;
      }).filter(Boolean).join(" / ");
      return prices ? `${market === "ah" ? "AH" : "Goals"}: ${prices}` : "";
    }).filter(Boolean).join("; ");

  const closingRecordTitle = (row) => {
    const updates = closingUpdateText(row).replace(/\n/g, ". ");
    const retrieved = formatProviderTime(row?.retrieved_at);
    const quotes = providerPricePairs(row);
    return `Provider closing prices. ${updates} (London time). Market update times may differ from when an individual price changed.${retrieved ? ` Retrieved ${retrieved}; retrieval is not the market-update time.` : ""}${quotes ? ` ${quotes}.` : ""}`;
  };

  const openingTimeText = (row) => {
    const ah = formatProviderTime(row?.market_opened_at?.ah);
    const totals = formatProviderTime(row?.market_opened_at?.totals);
    if (ah && ah === totals) return `Markets opened ${ah}`;
    return `AH opened: ${ah || "unavailable"}\nTG opened: ${totals || "unavailable"}`;
  };

  const openingRecordTitle = (row) => {
    const times = openingTimeText(row).replace(/\n/g, ". ");
    const retrieved = formatProviderTime(row?.retrieved_at);
    const quotes = providerPricePairs(row);
    return `Earliest ${row?.bookmaker || "bookmaker"} prices recorded by the provider. ${times} (London time). SUP and TG use the handicap and goals opening prices; the two markets may have opened at different times.${retrieved ? ` Retrieved ${retrieved}; retrieval is not the opening time.` : ""}${quotes ? ` ${quotes}.` : ""}`;
  };

  const rowTitle = (row) => {
    if (!row) return "No suitable stored snapshot.";
    if (row._providerOpening) return openingRecordTitle(row);
    if (row._providerClosing) return closingRecordTitle(row);
    const details = [`Captured ${formatDateLong(row.snapshot_utc)} (London time).`];
    if (row.state_source) details.push(`Match state: ${String(row.state_source).replace(/_/g, " ")}.`);
    if (row.calculation_status === "unverified_stoppage_time") {
      details.push("SUP and TG are unavailable because the announced stoppage time has not been confirmed.");
    } else if (row.calculation_status) {
      details.push(`Calculation: ${String(row.calculation_status).replace(/_/g, " ")}.`);
    }
    return details.join(" ");
  };

  const healthAge = numberOrNull(health?.age_seconds);
  const staleCollection = healthAge !== null && healthAge > 45 * 60;
  const healthProblem = [health?.status, health?.db].some((value) => ["error", "unavailable", "degraded"].includes(String(value).toLowerCase()));
  const selectedLeagueHealth = (Array.isArray(health?.leagues) ? health.leagues : []).find((league) => league.league === selectedLeague);

  const selectedFixtureInfo =
    fixtures.find((fx) => String(fx.fixture_id) === String(selectedFixture)) ||
    snapshots[0] ||
    {};

  const homeTeam =
    selectedFixtureInfo.home_team ||
    snapshots[0]?.home_team ||
    "Home team";

  const awayTeam =
    selectedFixtureInfo.away_team ||
    snapshots[0]?.away_team ||
    "Away team";

  const kickoff =
    selectedFixtureInfo.kickoff_utc ||
    snapshots[0]?.kickoff_utc ||
    "";

  const selectedMatchState = fixtureStatusView(selectedFixtureInfo);

  const cell = (value, align = "left", title = "") => (
    <td style={align === "right" ? styles.num : styles.td} title={title || undefined}>
      {isPresent(value) ? value : <span style={styles.blank}>—</span>}
    </td>
  );

  const scoreWithCards = (row) => {
    if (!row || row._providerClosing || row._providerOpening) return "";
    const home = numberOrNull(row.home_score);
    const away = numberOrNull(row.away_score);
    const cardCount = (value) => {
      const count = numberOrNull(value);
      return Number.isSafeInteger(count) && count >= 0 ? count : null;
    };
    const homeCards = cardCount(row.home_red_cards);
    const awayCards = cardCount(row.away_red_cards);
    const describeCards = (count) => count === null ? "red-card count unavailable"
      : count === 0 ? "no red cards" : `${count} red card${count === 1 ? "" : "s"}`;
    const description = `Home score ${home === null ? "unavailable" : home}; away score ${away === null ? "unavailable" : away}. Home: ${describeCards(homeCards)}. Away: ${describeCards(awayCards)}.`;
    const cards = (count, side) => (
      <span aria-hidden="true" style={{ display: "flex", justifyContent: side === "home" ? "flex-end" : "flex-start", alignItems: "center", flexWrap: "wrap", gap: "2px", minHeight: "11px" }}>
        {count === null ? <span style={{ color: "#94a3b8", fontSize: "11px" }}>?</span>
          : Array.from({ length: count }, (_, index) => <span key={index} style={{ display: "inline-block", width: "7px", height: "11px", background: "#dc2626", border: "1px solid #b91c1c", borderRadius: "1px", boxSizing: "border-box", flexShrink: 0 }} />)}
      </span>
    );
    return (
      <span role="img" aria-label={description} title={description} style={{ display: "inline-grid", gridTemplateColumns: "minmax(34px, 1fr) 2ch 1ch 2ch minmax(34px, 1fr)", columnGap: "3px", alignItems: "center", width: "100%", minWidth: "120px", textAlign: "center" }}>
        {cards(homeCards, "home")}
        <span aria-hidden="true" style={home === null ? styles.blank : undefined}>{home === null ? "—" : home}</span>
        <span aria-hidden="true">–</span>
        <span aria-hidden="true" style={away === null ? styles.blank : undefined}>{away === null ? "—" : away}</span>
        {cards(awayCards, "away")}
      </span>
    );
  };

  return (
    <div style={styles.page}>
      <div style={styles.controls}>
        <div>
          <label style={styles.label}>League:</label>
          <select
            style={styles.select}
            value={selectedLeague}
            onChange={(e) => {
              setSelectedLeague(e.target.value);
              setSelectedFixture(null);
              setFixtures([]);
              setSnapshots([]);
              setOpeningData(null);
              setOpeningError("");
              setClosingData(null);
              setClosingError("");
            }}
          >
            {leagues.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>

        <button style={styles.button} onClick={() => setRefreshVersion((value) => value + 1)} disabled={loading}>
          Reload
        </button>
        {loading && <span style={styles.muted}>Loading...</span>}
        {error && <span style={styles.error}>{error}</span>}
      </div>

      <div style={{ ...styles.muted, marginBottom: "14px" }} aria-live="polite">
        {health?.last_snapshot_utc && (
          <div>Last odds collected: {formatDateLong(health.last_snapshot_utc)} (London time). Refreshes every minute while visible.</div>
        )}
        {(staleCollection || healthProblem) && (
          <div role="status" style={{ color: "#92400e", marginTop: "5px", fontWeight: 700 }}>
            {staleCollection ? "No new odds have been collected for over 45 minutes. Displayed odds may be out of date." : "The odds service reports a problem. Displayed odds may be out of date."}
          </div>
        )}
        {health && !health.last_snapshot_utc && <div>No odds collection time is available.</div>}
        {healthError && <div style={{ color: "#92400e", marginTop: "5px" }}>{healthError}</div>}
        {selectedLeagueHealth?.last_snapshot_utc && (
          <div style={{ marginTop: "5px" }}>Latest in this league: {formatDateLong(selectedLeagueHealth.last_snapshot_utc)}. Leagues without scheduled matches may update less often.</div>
        )}
      </div>

      {fixtures.length > 0 && !selectedFixture && (
        <div style={styles.tableWrap}>
          <p style={styles.muted}>Match status and result are checked separately from the odds. Last odds and the score when saved describe the saved prices, even if the match has since finished. Red rectangles beside a saved score show red cards; ? means the card count is unavailable.</p>
          <table style={{ ...styles.table, width: "100%", minWidth: "1100px", fontSize: "12px" }}>
            <thead>
              <tr>
                <th style={styles.th}>Kickoff</th>
                <th style={styles.th}>Home</th>
                <th style={styles.th}>Away</th>
                <th style={styles.th}>Match status<br />&amp; result</th>
                <th style={styles.th} title="Stage of the most recently saved odds; this is not the current match status.">Last odds</th>
                <th style={styles.thNum}>Minute / before KO<br />when saved</th>
                <th style={styles.th} title="Home–away score, with home red cards on the left and away red cards on the right.">Score<br />when saved</th>
                <th style={styles.thNum} title={supDescription}>SUP<br />(Basis)</th>
                <th style={styles.thNum} title={tgDescription}>TG<br />(Basis)</th>
                <th style={styles.th}>Bookmaker</th>
                <th style={styles.th}>Odds saved</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {fixtures.map((fx, i) => (
                <tr key={`${fx.fixture_id}-${fx.bookmaker || i}`} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }} title={rowTitle(fx)}>
                  {cell(formatDate(fx.kickoff_utc))}
                  {cell(<span style={{ display: "block", minWidth: "75px", maxWidth: "125px", whiteSpace: "normal" }}>{fx.home_team}</span>)}
                  {cell(<span style={{ display: "block", minWidth: "75px", maxWidth: "125px", whiteSpace: "normal" }}>{fx.away_team}</span>)}
                  <td style={styles.td} title={fixtureStatusTitle(fx)}>
                    <div>{fixtureStatusView(fx).label}</div>
                    {fixtureStatusView(fx).score && <div style={{ ...styles.muted, fontSize: "11px", marginTop: "3px" }} title={currentScoreTitle(fx)}>{fixtureStatusView(fx).scoreLabel} <strong>{fixtureStatusView(fx).score}</strong></div>}
                  </td>
                  <td style={styles.td}>
                    <span style={matchStage(fx) === "live" ? styles.pillLive : styles.pillPre}>
                      {lastOddsStage(fx)}
                    </span>
                  </td>
                  {cell(matchStage(fx) === "pre" ? formatActualPre(fx) : formatClock(fx), "right")}
                  {cell(scoreWithCards(fx), "right", `Home–away score and red cards when these odds were saved: ${formatDateLong(fx.snapshot_utc)} (London time).`)}
                  {cell(metric(fx, "sup"), "right", supDescription)}
                  {cell(metric(fx, "tg"), "right", tgDescription)}
                  {cell(fx.bookmaker)}
                  {cell(formatDate(fx.snapshot_utc))}
                  <td style={styles.td}>
                    <button
                      style={styles.button}
                      onClick={() => {
                        setSnapshots([]);
                        setOpeningData(null);
                        setOpeningError("");
                        setClosingData(null);
                        setClosingError("");
                        setSelectedFixture(fx.fixture_id);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedFixture && (
        <div>
          <button
            style={styles.backButton}
            onClick={() => {
              setSelectedFixture(null);
              setSnapshots([]);
              setOpeningData(null);
              setOpeningError("");
              setClosingData(null);
              setClosingError("");
            }}
          >
            ← Back to fixtures
          </button>

          <div style={styles.card}>
            <div style={styles.header}>
              <div>
                <div style={styles.title}>
                  <span>{homeTeam}</span>
                  <span style={styles.versus}>v</span>
                  <span>{awayTeam}</span>
                </div>
                <div style={styles.subtitle}>
                  {selectedLeague}
                  {kickoff ? ` • ${formatDateLong(kickoff)}` : ""}
                </div>
                <div style={styles.subtitle} title={fixtureStatusTitle(selectedFixtureInfo)}>
                  Match status: <strong>{selectedMatchState.label}</strong>
                  {selectedMatchState.score && <span title={currentScoreTitle(selectedFixtureInfo)}> • {selectedMatchState.scoreLabel}: <strong>{selectedMatchState.score}</strong></span>}
                </div>
                {selectedMatchState.observedAt && <div style={{ ...styles.muted, marginTop: "5px" }}>Status checked {formatDateLong(selectedMatchState.observedAt)} (London time).</div>}
                {selectedMatchState.score && selectedMatchState.scoreObservedAt !== selectedMatchState.observedAt && <div style={{ ...styles.muted, marginTop: "5px" }}>Score checked {formatDateLong(selectedMatchState.scoreObservedAt)} (London time).</div>}
                {selectedFixtureInfo.snapshot_utc && <div style={{ ...styles.muted, marginTop: "5px" }}>Last odds: {lastOddsStage(selectedFixtureInfo)}, saved {formatDateLong(selectedFixtureInfo.snapshot_utc)}.</div>}
              </div>

              <div style={styles.note}>
                SUP and TG show the yellow Basis values from your Poisson sheet, adjusted for the match clock. Negative SUP favours the away team. Saved scores are home–away; red rectangles show each team’s red cards, and ? means an unavailable card count.
              </div>
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Pre-match</h3>
              <p style={styles.muted}>Opening uses the earliest {openingRow?.bookmaker || "SingBet"} prices recorded by the provider. Handicap and goals may open at different times.</p>
              <p style={styles.muted}>Each checkpoint uses the nearest saved price within 5 minutes. Closing uses the provider’s closing-price archive. Until it is available, the last collected pre-match price is shown separately as unconfirmed.</p>
              {openingError && <p style={{ ...styles.muted, color: "#92400e" }}>{openingError}{openingRow ? " The previously retrieved opening prices are still shown." : ""}</p>}
              {closingError && <p style={{ ...styles.muted, color: "#92400e" }}>{closingError}{closingRow ? " The previously retrieved archive is still shown." : ""}</p>}
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Time before KO</th>
                      <th style={styles.thNum} title={supDescription}>SUP (Basis)</th>
                      <th style={styles.thNum} title={tgDescription}>TG (Basis)</th>
                      <th style={styles.th}>Bookmaker</th>
                      <th style={styles.th} title="Checkpoints show capture time. Opening and Closing show the provider’s market times.">Price time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preMatchRows.map(({ target, row }, i) => (
                      <tr key={target.label} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }} title={rowTitle(row)}>
                        {cell(target.label, "left", target.opening
                          ? row ? openingRecordTitle(row) : "Waiting for explicit provider opening prices for both handicap and goals; the first locally collected quote is not used as Opening."
                          : target.closing
                          ? row ? closingRecordTitle(row) : "Waiting for the provider’s confirmed closing-price archive; a collected pre-match quote is not confirmed closing."
                          : row ? `${target.provisional ? "Last collected pre-match price; not confirmed closing. " : ""}Actual timing: ${formatActualPre(row)}.` : "No saved price within 5 minutes of this checkpoint.")}
                        {cell(metric(row, "sup"), "right", supDescription)}
                        {cell(metric(row, "tg"), "right", tgDescription)}
                        {cell(row?.bookmaker)}
                        {cell(target.opening
                          ? row ? <span style={{ whiteSpace: "pre-line" }}>{openingTimeText(row)}</span> : openingStatusText
                          : target.closing
                          ? row ? <span style={{ whiteSpace: "pre-line" }}>{closingUpdateText(row)}</span> : closingStatusText
                          : row ? formatDate(row.snapshot_utc) : "", "left", row && target.opening ? openingRecordTitle(row) : target.closing && row ? closingRecordTitle(row) : "")}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>In-play</h3>
              <p style={styles.muted}>0 uses the confirmed closing prices, without a match score or red-card count. The following rows select the closest saved price around each two-minute checkpoint and show its actual match minute. Grey times mark gaps; added-time prices are kept.</p>
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Time</th>
                      <th style={styles.th} title="Home–away score, with home red cards on the left and away red cards on the right, when these odds were saved.">Score when saved</th>
                      <th style={styles.thNum} title={supDescription}>SUP (Basis)</th>
                      <th style={styles.thNum} title={tgDescription}>TG (Basis)</th>
                      <th style={styles.th}>Bookmaker</th>
                      <th style={styles.th} title="0 shows the provider’s closing-market update time. Other rows show when the price was captured.">Price time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inPlayRows.map(({ target, row }, i) => (
                      <tr key={target.key || target.label} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }} title={rowTitle(row)}>
                        <td style={styles.td} title={row ? target.closing ? closingRecordTitle(row) : rowTitle(row)
                          : target.closing ? "Waiting for confirmed provider closing prices; no live price is used for 0."
                            : target.status ? `${target.label} is a placeholder; no confirmed price at this match status was recorded.`
                              : `${target.label} is a checkpoint placeholder, not an observed minute. No suitable price was recorded in this two-minute interval.`}>
                          <span style={row ? undefined : styles.blank}>{inPlayRowValues(target, row).time}</span>
                        </td>
                        {cell(target.closing ? "" : scoreWithCards(row), "right")}
                        {cell(metric(row, "sup"), "right", supDescription)}
                        {cell(metric(row, "tg"), "right", tgDescription)}
                        {cell(row?.bookmaker)}
                        {cell(target.closing
                          ? row ? <span style={{ whiteSpace: "pre-line" }}>{closingUpdateText(row)}</span> : closingStatusText
                          : row ? formatDate(row.snapshot_utc) : "", "left", target.closing && row ? closingRecordTitle(row) : "")}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {otherInPlayRows.length > 0 && (
              <details style={styles.section}>
                <summary style={{ ...styles.muted, cursor: "pointer" }}>Other stored in-play prices ({otherInPlayRows.length})</summary>
                <p style={styles.muted}>These include extra prices between checkpoints and prices whose half could not be confirmed. They are listed by capture time.</p>
                <div style={styles.tableWrap}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Captured</th>
                        <th style={styles.th}>Time</th>
                        <th style={styles.th}>Half</th>
                        <th style={styles.th} title="Home–away score, with home red cards on the left and away red cards on the right.">Score when saved</th>
                        <th style={styles.thNum} title={supDescription}>SUP (Basis)</th>
                        <th style={styles.thNum} title={tgDescription}>TG (Basis)</th>
                        <th style={styles.th}>Bookmaker</th>
                      </tr>
                    </thead>
                    <tbody>
                      {otherInPlayRows.map((row, i) => (
                        <tr key={`${row.snapshot_utc}-${row.bookmaker}-${i}`} title={rowTitle(row)} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                          {cell(formatProviderTime(row.snapshot_utc))}
                          {cell(clockMinute(row) === 0 && matchStage(row) === "live" ? "Start of play" : formatClock(row))}
                          {cell(clockHalf(row) === 1 ? "First" : clockHalf(row) === 2 ? "Second" : "Unconfirmed")}
                          {cell(scoreWithCards(row), "right")}
                          {cell(metric(row, "sup"), "right", supDescription)}
                          {cell(metric(row, "tg"), "right", tgDescription)}
                          {cell(row.bookmaker)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            {unknownClockRows.length > 0 && (
              <details style={styles.section}>
                <summary style={{ ...styles.muted, cursor: "pointer" }}>
                  Match minute unavailable ({unknownClockRows.length} stored snapshots)
                </summary>
                <p style={styles.muted}>These snapshots have no confirmed match clock. Their capture times are shown below; SUP and TG cannot be calculated safely.</p>
                <div style={styles.tableWrap}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Captured</th>
                        <th style={styles.th}>Actual min</th>
                        <th style={styles.th} title="Home–away score, with home red cards on the left and away red cards on the right.">Score when saved</th>
                        <th style={styles.thNum} title={supDescription}>SUP (Basis)</th>
                        <th style={styles.thNum} title={tgDescription}>TG (Basis)</th>
                        <th style={styles.th}>Bookmaker</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unknownClockRows.map((row, i) => (
                        <tr key={`${row.snapshot_utc}-${row.bookmaker}-${i}`} title={rowTitle(row)} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                          {cell(formatDateLong(row.snapshot_utc))}
                          {cell("")}
                          {cell(scoreWithCards(row), "right")}
                          {cell("", "right")}
                          {cell("", "right")}
                          {cell(row.bookmaker)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p style={{ ...styles.muted, marginTop: "14px" }}>
              In-play rows show actual recorded minutes. Grey checkpoint times are placeholders, and a dash means missing data or an unavailable calculation, never zero. 0 is the confirmed closing price; it is not a live capture. HT and FT require a confirmed match status. Added-time SUP/TG are blank when the announced stoppage time is not available. All price times are London time.
            </p>
          </div>

          {snapshots.length === 0 && !loading && (
            <p style={styles.muted}>No snapshots for this fixture.</p>
          )}
        </div>
      )}

      {fixtures.length === 0 && !loading && !error && (
        <p style={styles.muted}>No odds data available yet. The pipeline will populate data as matches approach.</p>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  Line-Ups Tab (original CSL dashboard)
// ════════════════════════════════════════════════════════════════

function LineupsTab() {
  const API_URL = process.env.REACT_APP_API_URL || "/api";

  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [season, setSeason]           = useState(null);
  const [matrix, setMatrix]           = useState([]);
  const [clubs, setClubs]             = useState([]);
  const [selectedClub, setSelectedClub] = useState("");
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");
  const [playerLabels, setPlayerLabels] = useState(() => {
    try { return JSON.parse(localStorage.getItem("playerLabels") || "{}"); }
    catch { return {}; }
  });

  const labelOptions = ["Key", "Imp", "Reg", "Sub"];

  useEffect(() => {
    fetch(`${API_URL}/seasons`)
      .then((r) => r.json())
      .then((d) => {
        const seasons = Array.isArray(d.seasons) ? d.seasons : [];
        setAvailableSeasons(seasons);
        if (seasons.length > 0) setSeason(seasons[0]);
      })
      .catch(() => {
        setAvailableSeasons([2025]);
        setSeason(2025);
      });
  }, [API_URL]);

  useEffect(() => {
    if (season === null) return;
    setLoading(true);
    setError("");
    setMatrix([]);
    setClubs([]);
    setSelectedClub("");

    fetch(`${API_URL}/lineups-matrix?season=${season}&v=${Date.now()}`)
      .then((r) => r.json())
      .then((data) => {
        const safe = Array.isArray(data) ? data : [];
        if (!Array.isArray(data) && data && data.error) {
          setError(`Season ${season}: ${data.error}`);
        }
        setMatrix(safe);
        const uniqueClubs = [
          ...new Map(safe.map((row) => [row.club, row.club_name])).entries(),
        ].sort((a, b) => a[1].localeCompare(b[1]));
        setClubs(uniqueClubs);
        if (uniqueClubs.length) setSelectedClub(uniqueClubs[0][0]);
        setLoading(false);
      })
      .catch((err) => {
        setError("Failed to load data. Please refresh.");
        setLoading(false);
        console.error("API error:", err);
      });
  }, [season, API_URL]);

  const SKIP_COLS = new Set(["player", "position", "club", "club_name", "club_slug", "club_id"]);

  const clubRows = matrix.filter((row) => row.club === selectedClub);

  const playerMap = {};
  clubRows.forEach((row) => {
    const key = `${row.player}||${row.club}`;
    if (!playerMap[key]) {
      playerMap[key] = {
        player: row.player,
        club: row.club,
        club_name: row.club_name,
        positionCounts: {},
        matchData: {},
      };
    }
    playerMap[key].positionCounts[row.position] =
      (playerMap[key].positionCounts[row.position] || 0) + 1;
    Object.entries(row).forEach(([k, v]) => {
      if (!SKIP_COLS.has(k) && v && String(v).trim()) {
        playerMap[key].matchData[k] = v;
      }
    });
  });

  const filtered = Object.values(playerMap).map((entry) => {
    const pos = Object.entries(entry.positionCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
    return { player: entry.player, club: entry.club, club_name: entry.club_name, position: pos, ...entry.matchData };
  });

  const allMatchKeySet = new Set();
  for (const row of filtered) {
    for (const k of Object.keys(row)) {
      if (!SKIP_COLS.has(k)) allMatchKeySet.add(k);
    }
  }

  const getIso = (s) => { const m = String(s).match(/^(\d{4}-\d{2}-\d{2})\s*\|/); return m ? m[1] : "0000-00-00"; };
  const matchKeys = Array.from(allMatchKeySet).sort((a, b) => getIso(b) > getIso(a) ? 1 : getIso(b) < getIso(a) ? -1 : 0);

  const prettyHeader = (label) => {
    const m = String(label).match(/^(\d{4}-\d{2}-\d{2})\s*\|\s*(.*)$/);
    if (!m) return label;
    const [, iso, rest] = m;
    const d = new Date(iso + "T00:00:00Z");
    if (isNaN(d.getTime())) return rest;
    const nice = d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
    const vs = rest.replace(/^\w{3},\s*\d{1,2}\/\d{1,2}\/\d{2,4}\s*/i, "").trim();
    return `${nice} ${vs}`;
  };

  const positionOrder = [
    "Goalkeeper","Right-Back","Left-Back","Centre-Back","Defender",
    "Defensive Midfield","Central Midfield","Midfield","Left Midfield",
    "Right Midfield","Attacking Midfield","Right Winger","Left Winger",
    "Centre-Forward","Attacker","Attack",
  ];
  const sorted = [...filtered].sort((a, b) => {
    const pa = positionOrder.indexOf(a.position);
    const pb = positionOrder.indexOf(b.position);
    return (pa === -1 ? 99 : pa) - (pb === -1 ? 99 : pb) || a.player.localeCompare(b.player);
  });

  const handleLabelChange = (player, label) => {
    const updated = { ...playerLabels, [player]: label };
    setPlayerLabels(updated);
    localStorage.setItem("playerLabels", JSON.stringify(updated));
  };

  const getBadgeClass = (label) => {
    const map = { Key: "label-badge label-key", Imp: "label-badge label-imp", Reg: "label-badge label-reg", Sub: "label-badge label-sub" };
    return map[label] || "";
  };

  return (
    <div>
      <div className="flex gap-6 mb-6 items-end flex-wrap">
        <div>
          <label className="block mb-1 text-sm font-medium">Season:</label>
          <select
            className="p-2 border rounded"
            value={season || ""}
            onChange={(e) => setSeason(Number(e.target.value))}
            disabled={availableSeasons.length === 0}
          >
            {availableSeasons.map((yr) => (
              <option key={yr} value={yr}>{yr}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block mb-1 text-sm font-medium">Team:</label>
          <select
            className="p-2 border rounded"
            value={selectedClub}
            onChange={(e) => setSelectedClub(e.target.value)}
            disabled={clubs.length === 0}
          >
            {clubs.length === 0 && <option value="">— loading —</option>}
            {clubs.map(([href, name]) => (
              <option key={href} value={href}>{name}</option>
            ))}
          </select>
        </div>

        {loading && <span className="text-sm text-gray-500 self-center">Loading...</span>}
        {error  && <span className="text-sm text-red-600 self-center">{error}</span>}
      </div>

      {!loading && sorted.length > 0 && (
        <div className="overflow-x-auto">
          <table className="table-auto text-sm border mt-4">
            <thead>
              <tr>
                <th className="border p-2 whitespace-nowrap">Player</th>
                <th className="border p-2 whitespace-nowrap">Position</th>
                <th className="border p-2">Label</th>
                {matchKeys.map((match) => (
                  <th key={match} className="border p-2 text-xs whitespace-nowrap">
                    {prettyHeader(match)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="border p-2 whitespace-nowrap">
                    <span className={getBadgeClass(playerLabels[row.player])}>
                      {row.player}
                    </span>
                  </td>
                  <td className="border p-2 whitespace-nowrap">{row.position}</td>
                  <td className="border p-2">
                    <select
                      className="p-1 text-xs"
                      value={playerLabels[row.player] || ""}
                      onChange={(e) => handleLabelChange(row.player, e.target.value)}
                    >
                      <option value="">-</option>
                      {labelOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </td>
                  {matchKeys.map((match, j) => (
                    <td key={j} className="border p-2 text-center">
                      {row[match] || ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && sorted.length === 0 && !error && selectedClub && (
        <p className="text-gray-500 mt-4">No lineup data for this team / season.</p>
      )}

      {/* Tailwind purge protection */}
      <div style={{display: "none"}}>
        <span className="bg-yellow-300 text-yellow-900"></span>
        <span className="bg-green-300 text-green-900"></span>
        <span className="bg-blue-300 text-blue-900"></span>
        <span className="bg-red-300 text-red-900"></span>
      </div>
    </div>
  );
}



// ════════════════════════════════════════════════════════════════
//  Montrose Trading Dark SportMonks Tab
// ════════════════════════════════════════════════════════════════

function MontroseBrandMark({ size = 42 }) {
  return (
    <svg
      className="smx-brand-mark"
      width={size}
      height={size}
      viewBox="0 0 48 48"
      role="img"
      aria-label="Montrose Trading"
    >
      <path d="M6 38V16l9 10 9-18 9 18 9-10v22" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 38l9-12 9 12 9-12 9 12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.78" />
    </svg>
  );
}

function SportMonksPreviewTab() {
  const API_URL = "/sportmonks-api";

  const [leagues, setLeagues] = useState([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState("");
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [matrix, setMatrix] = useState([]);
  const [qualityCount, setQualityCount] = useState(null);
  const [qualityItems, setQualityItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [playerLabels, setPlayerLabels] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sportmonksPlayerLabels") || "{}");
    } catch {
      return {};
    }
  });

  const labelOptions = ["Key", "Imp", "Reg", "Sub"];

  const selectedLeague = leagues.find(
    (league) => String(league.id) === String(selectedLeagueId)
  );
  const seasonId = selectedLeague?.current_season_id || "";

  const fetchJson = async (url) => {
    const response = await fetch(url, { cache: "no-store" });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.error || `Request failed (${response.status})`);
    }
    return data;
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    fetchJson(`${API_URL}/leagues`)
      .then((data) => {
        if (cancelled) return;
        const safe = Array.isArray(data?.leagues) ? data.leagues : [];
        setLeagues(safe);
        const preferred = safe.find((league) => Number(league.id) === 989) || safe[0];
        setSelectedLeagueId(preferred ? String(preferred.id) : "");
      })
      .catch((err) => {
        if (!cancelled) setError(`Could not load SportMonks leagues: ${err.message}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedLeagueId || !seasonId) return;

    let cancelled = false;
    setLoading(true);
    setError("");
    setTeams([]);
    setSelectedTeamId("");
    setMatrix([]);
    setQualityCount(null);
    setQualityItems([]);

    Promise.all([
      fetchJson(
        `${API_URL}/teams?league_id=${encodeURIComponent(selectedLeagueId)}` +
        `&season_id=${encodeURIComponent(seasonId)}`
      ),
      fetchJson(
        `${API_URL}/quality?league_id=${encodeURIComponent(selectedLeagueId)}` +
        `&season_id=${encodeURIComponent(seasonId)}`
      ),
    ])
      .then(([teamData, qualityData]) => {
        if (cancelled) return;
        const safeTeams = Array.isArray(teamData?.teams) ? teamData.teams : [];
        setTeams(safeTeams);
        const preferredTeam =
          safeTeams.find((team) => team.name === "Shanghai Port") || safeTeams[0];
        setSelectedTeamId(preferredTeam ? String(preferredTeam.id) : "");
        setQualityCount(Number(qualityData?.invalid_starter_groups || 0));
        setQualityItems(Array.isArray(qualityData?.items) ? qualityData.items : []);
      })
      .catch((err) => {
        if (!cancelled) setError(`Could not load SportMonks teams: ${err.message}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedLeagueId, seasonId]);

  useEffect(() => {
    if (!selectedLeagueId || !seasonId || !selectedTeamId) return;

    let cancelled = false;
    setLoading(true);
    setError("");
    setMatrix([]);

    fetchJson(
      `${API_URL}/lineups-matrix?league_id=${encodeURIComponent(selectedLeagueId)}` +
      `&season_id=${encodeURIComponent(seasonId)}` +
      `&team_id=${encodeURIComponent(selectedTeamId)}` +
      `&v=${Date.now()}`
    )
      .then((data) => {
        if (cancelled) return;
        if (!Array.isArray(data)) {
          throw new Error(data?.error || "Unexpected lineup response");
        }
        setMatrix(data);
        setLastLoadedAt(new Date());
      })
      .catch((err) => {
        if (!cancelled) setError(`Could not load SportMonks lineups: ${err.message}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedLeagueId, seasonId, selectedTeamId]);

  const SKIP_COLS = new Set([
    "player_id",
    "player",
    "position",
    "club",
    "club_name",
    "club_slug",
    "club_id",
  ]);

  const playerMap = {};
  matrix.forEach((row) => {
    const stableKey = `${row.club_id || row.club}||${row.player_id || row.player}`;
    if (!playerMap[stableKey]) {
      playerMap[stableKey] = {
        player_id: row.player_id || row.player,
        player: row.player,
        club: row.club,
        club_id: row.club_id,
        club_name: row.club_name,
        positionCounts: {},
        matchData: {},
      };
    }

    if (row.position) {
      playerMap[stableKey].positionCounts[row.position] =
        (playerMap[stableKey].positionCounts[row.position] || 0) + 1;
    }

    Object.entries(row).forEach(([key, value]) => {
      if (!SKIP_COLS.has(key) && value && String(value).trim()) {
        playerMap[stableKey].matchData[key] = value;
      }
    });
  });

  const filtered = Object.values(playerMap).map((entry) => {
    const position =
      Object.entries(entry.positionCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
    return {
      player_id: entry.player_id,
      player: entry.player,
      club: entry.club,
      club_id: entry.club_id,
      club_name: entry.club_name,
      position,
      ...entry.matchData,
    };
  });

  const allMatchKeySet = new Set();
  filtered.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!SKIP_COLS.has(key)) allMatchKeySet.add(key);
    });
  });

  const getIso = (label) => {
    const match = String(label).match(/^(\d{4}-\d{2}-\d{2})\s*\|/);
    return match ? match[1] : "0000-00-00";
  };

  const matchKeys = Array.from(allMatchKeySet).sort((a, b) =>
    getIso(b) > getIso(a) ? 1 : getIso(b) < getIso(a) ? -1 : 0
  );

  const parseMatchHeader = (label) => {
    const parts = String(label).split("|").map((part) => part.trim());
    const iso = parts[0] || "";
    const date = new Date(`${iso}T00:00:00Z`);
    const dateText = Number.isNaN(date.getTime())
      ? iso
      : date.toLocaleDateString("en-GB", {
          weekday: "short",
          day: "2-digit",
          month: "short",
          timeZone: "UTC",
        });

    const resultPart = parts.find((part) => /^[WLD]\s+\d+\s*[-–]\s*\d+$/i.test(part)) || "";
    const opponentPart = parts.find((part) => /^vs\s+/i.test(part)) || "";
    const outcome = resultPart ? resultPart.charAt(0).toUpperCase() : "";

    return {
      dateText,
      resultText: resultPart.replace("-", "–"),
      opponentText: opponentPart,
      outcome,
    };
  };

  const positionSortRank = (position) => {
    const compact = String(position || "")
      .trim()
      .toLowerCase()
      .replace(/[–—_\s-]/g, "");

    if (compact === "gk" || compact.includes("goalkeeper")) return 0;

    if (compact === "rb" || compact.includes("rightback")) return 10;
    if (compact === "rwb" || compact.includes("rightwingback")) return 11;
    if (
      compact === "cb" ||
      compact.includes("centreback") ||
      compact.includes("centerback")
    ) return 12;
    if (compact === "sw" || compact.includes("sweeper")) return 13;
    if (compact === "lb" || compact.includes("leftback")) return 14;
    if (compact === "lwb" || compact.includes("leftwingback")) return 15;
    if (compact === "df" || compact.includes("defender")) return 19;

    if (
      compact === "dm" ||
      compact === "cdm" ||
      compact.includes("defensivemidfield")
    ) return 20;
    if (
      compact === "cm" ||
      compact.includes("centralmidfield") ||
      compact.includes("centremidfield")
    ) return 21;
    if (
      compact === "cam" ||
      compact === "am" ||
      compact.includes("attackingmidfield")
    ) return 22;
    if (compact === "rm" || compact.includes("rightmidfield")) return 23;
    if (compact === "lm" || compact.includes("leftmidfield")) return 24;
    if (compact === "mf" || compact.includes("midfield")) return 29;

    // Wingers deliberately appear before centre-forwards and strikers.
    if (
      compact === "rw" ||
      compact === "rightwing" ||
      compact.includes("rightwinger")
    ) return 30;
    if (
      compact === "lw" ||
      compact === "leftwing" ||
      compact.includes("leftwinger")
    ) return 31;

    if (compact === "ss" || compact.includes("secondstriker")) return 32;
    if (
      compact === "cf" ||
      compact.includes("centreforward") ||
      compact.includes("centerforward")
    ) return 33;
    if (compact === "st" || compact.includes("striker")) return 34;
    if (
      compact === "fw" ||
      compact.includes("forward") ||
      compact.includes("attacker") ||
      compact === "attack"
    ) return 35;

    return 99;
  };

  const sorted = [...filtered].sort((a, b) => {
    const rankDifference =
      positionSortRank(a.position) - positionSortRank(b.position);

    if (rankDifference !== 0) return rankDifference;

    return String(a.player || "").localeCompare(
      String(b.player || "")
    );
  });

  const labelStorageKey = (row) =>
    `${row.club_id || selectedTeamId}:${row.player_id || row.player}`;

  const handleLabelChange = (row, label) => {
    const key = labelStorageKey(row);
    const updated = { ...playerLabels, [key]: label };
    setPlayerLabels(updated);
    localStorage.setItem("sportmonksPlayerLabels", JSON.stringify(updated));
  };

  const positionMeta = (position) => {
    const raw = String(position || "").trim();
    const value = raw
      .toLowerCase()
      .replace(/[–—_]/g, "-")
      .replace(/\s+/g, " ");
    const compact = value.replace(/[\s-]/g, "");
    const suppliedCode = raw.toUpperCase();

    const knownCodes = {
      GK: ["GK", "smx-pos-gk"],
      RB: ["RB", "smx-pos-df"],
      RWB: ["RWB", "smx-pos-df"],
      CB: ["CB", "smx-pos-df"],
      LB: ["LB", "smx-pos-df"],
      LWB: ["LWB", "smx-pos-df"],
      SW: ["SW", "smx-pos-df"],
      DF: ["DF", "smx-pos-df"],
      DM: ["DM", "smx-pos-mf"],
      CM: ["CM", "smx-pos-mf"],
      CAM: ["CAM", "smx-pos-mf"],
      AM: ["CAM", "smx-pos-mf"],
      RM: ["RM", "smx-pos-mf"],
      LM: ["LM", "smx-pos-mf"],
      MF: ["MF", "smx-pos-mf"],
      RW: ["RW", "smx-pos-fw"],
      LW: ["LW", "smx-pos-fw"],
      CF: ["CF", "smx-pos-fw"],
      ST: ["ST", "smx-pos-fw"],
      SS: ["SS", "smx-pos-fw"],
      FW: ["FW", "smx-pos-fw"],
    };

    if (knownCodes[suppliedCode]) {
      const [code, className] = knownCodes[suppliedCode];
      return { code, className };
    }

    if (compact.includes("goalkeeper")) return { code: "GK", className: "smx-pos-gk" };

    if (compact.includes("rightwingback")) return { code: "RWB", className: "smx-pos-df" };
    if (compact.includes("leftwingback")) return { code: "LWB", className: "smx-pos-df" };
    if (compact.includes("rightback")) return { code: "RB", className: "smx-pos-df" };
    if (compact.includes("leftback")) return { code: "LB", className: "smx-pos-df" };
    if (compact.includes("centreback") || compact.includes("centerback")) {
      return { code: "CB", className: "smx-pos-df" };
    }
    if (compact.includes("sweeper")) return { code: "SW", className: "smx-pos-df" };
    if (compact.includes("defender")) return { code: "DF", className: "smx-pos-df" };

    if (compact.includes("defensivemidfield")) return { code: "DM", className: "smx-pos-mf" };
    if (compact.includes("attackingmidfield")) return { code: "CAM", className: "smx-pos-mf" };
    if (compact.includes("centralmidfield") || compact.includes("centremidfield")) {
      return { code: "CM", className: "smx-pos-mf" };
    }
    if (compact.includes("rightmidfield")) return { code: "RM", className: "smx-pos-mf" };
    if (compact.includes("leftmidfield")) return { code: "LM", className: "smx-pos-mf" };
    if (compact.includes("midfield")) return { code: "MF", className: "smx-pos-mf" };

    if (compact.includes("rightwinger") || compact == "rightwing") {
      return { code: "RW", className: "smx-pos-fw" };
    }
    if (compact.includes("leftwinger") || compact == "leftwing") {
      return { code: "LW", className: "smx-pos-fw" };
    }
    if (compact.includes("centreforward") || compact.includes("centerforward")) {
      return { code: "CF", className: "smx-pos-fw" };
    }
    if (compact.includes("secondstriker")) return { code: "SS", className: "smx-pos-fw" };
    if (compact.includes("striker")) return { code: "ST", className: "smx-pos-fw" };
    if (compact.includes("forward")) return { code: "FW", className: "smx-pos-fw" };
    if (compact.includes("winger")) return { code: "FW", className: "smx-pos-fw" };
    if (compact.includes("attacker") || compact == "attack") {
      return { code: "FW", className: "smx-pos-fw" };
    }

    return { code: raw ? raw.slice(0, 3).toUpperCase() : "—", className: "smx-pos-mf" };
  };

  const flagFor = (country) => {
    const flags = {
      China: "🇨🇳",
      Norway: "🇳🇴",
      Belgium: "🇧🇪",
      England: "🇬🇧",
      France: "🇫🇷",
    };
    return flags[country] || "⚽";
  };

  const selectedTeam = teams.find(
    (team) => String(team.id) === String(selectedTeamId)
  );

  const loadedText = lastLoadedAt
    ? lastLoadedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <section className="smx-preview-panel">
      <div className="smx-preview-heading">
        <div className="smx-preview-heading-copy">
          <div className="smx-mini-mark"><MontroseBrandMark size={30} /></div>
          <div>
            <h1>SportMonks Preview</h1>
            <p>Five-league lineup and match-event data for the Montrose trading workflow.</p>
          </div>
        </div>

        <div className="smx-source-card">
          <div className="smx-source-title"><span className="smx-source-dot"></span> Source: SportMonks</div>
          <div>Team: {selectedTeam?.name || "—"}</div>
          <div>Last loaded: {loadedText}</div>
        </div>
      </div>

      <div className="smx-filter-row">
        <label>
          <span>League</span>
          <select
            value={selectedLeagueId}
            onChange={(event) => setSelectedLeagueId(event.target.value)}
            disabled={leagues.length === 0}
          >
            {leagues.length === 0 && <option value="">— loading —</option>}
            {leagues.map((league) => (
              <option key={league.id} value={league.id}>
                {flagFor(league.country_name)} {league.country_name} — {league.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Season</span>
          <select value={seasonId || ""} disabled>
            <option value={seasonId || ""}>
              {selectedLeague?.season_name || "Current season"}
            </option>
          </select>
        </label>

        <label>
          <span>Team</span>
          <select
            value={selectedTeamId}
            onChange={(event) => setSelectedTeamId(event.target.value)}
            disabled={teams.length === 0}
          >
            {teams.length === 0 && <option value="">— loading —</option>}
            {teams.map((team) => (
              <option key={team.id} value={team.id}>{team.name}</option>
            ))}
          </select>
        </label>

        <div className="smx-filter-status">
          {loading && <span className="smx-loading">Loading data…</span>}
          {error && <span className="smx-error">{error}</span>}
          {!loading && !error && <span><span className="smx-live-dot"></span> Data ready</span>}
        </div>
      </div>

      <div className="smx-legend-row">
        <strong>EVENT LEGEND</strong>
        <span>✅ Started</span>
        <span>◻️ Unused sub</span>
        <span>🔼 Came on</span>
        <span>🔁off Subbed off</span>
        <span>⚽ Goal</span>
        <span>🟨 Yellow card</span>
        <span>🟥 Red card</span>
        <em>Hover over a position badge for the full role</em>
      </div>

      {qualityCount !== null && (
        <div className={`smx-quality-row ${qualityCount > 0 ? "smx-quality-warning" : "smx-quality-ok"}`}>
          <span className="smx-quality-icon">{qualityCount > 0 ? "!" : "✓"}</span>
          {qualityCount > 0
            ? `${qualityCount} team-match lineup group${qualityCount === 1 ? "" : "s"} do not currently show exactly 11 starters. ${qualityItems.length > 0 ? "They remain visible for review." : ""}`
            : "Starter-count quality check passed for every imported team-match lineup in this league."}
        </div>
      )}

      {!loading && sorted.length > 0 && (
        <div className="smx-table-frame">
          <div className="smx-table-scroll">
            <table className="smx-matrix-table">
              <thead>
                <tr>
                  <th>PLAYER</th>
                  <th>POSITION</th>
                  <th>LABEL</th>
                  {matchKeys.map((match) => {
                    const header = parseMatchHeader(match);
                    return (
                      <th key={match} className="smx-match-header">
                        <span className="smx-match-date">{header.dateText}</span>
                        {header.resultText && (
                          <span className={`smx-match-result smx-result-${header.outcome.toLowerCase()}`}>
                            {header.resultText}
                          </span>
                        )}
                        <span className="smx-match-opponent">{header.opponentText}</span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sorted.map((row) => {
                  const storedLabel = playerLabels[labelStorageKey(row)] || "";
                  const meta = positionMeta(row.position);
                  return (
                    <tr key={`${row.club_id}:${row.player_id || row.player}`}>
                      <td className="smx-player-cell">
                        <span>{row.player}</span>
                      </td>
                      <td className="smx-position-cell" title={row.position || "Position unavailable"}>
                        <span className={`smx-position-badge ${meta.className}`}>{meta.code}</span>
                      </td>
                      <td className="smx-label-cell">
                        <select
                          className="smx-label-select"
                          value={storedLabel}
                          onChange={(event) => handleLabelChange(row, event.target.value)}
                          aria-label={`Label for ${row.player}`}
                        >
                          <option value="">-</option>
                          {labelOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </td>
                      {matchKeys.map((match) => {
                        const value = row[match] || "";
                        return (
                          <td key={match} className="smx-event-cell" title={value || "No lineup event"}>
                            <span className={value ? "smx-event-symbols" : "smx-no-data"}>
                              {value || "—"}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && sorted.length === 0 && !error && selectedTeamId && (
        <div className="smx-empty-state">No imported lineup data for this team and season.</div>
      )}

      <footer className="smx-panel-footer">
        <span>Data stored on your Montrose AWS server</span>
        <span><span className="smx-live-dot"></span> Automatic SportMonks updates active</span>
        <span>{sorted.length} players · {matchKeys.length} matches</span>
      </footer>
    </section>
  );
}


// ════════════════════════════════════════════════════════════════
//  Main App with Tab Navigation
// ════════════════════════════════════════════════════════════════

function App() {
  const [activeTab, setActiveTab] = useState("sportmonks");

  const tabs = [
    { id: "lineups", label: "Line-Ups" },
    { id: "sportmonks", label: "SportMonks Preview" },
    { id: "odds", label: "Odds Supremacy" },
  ];

  if (activeTab === "sportmonks") {
    return (
      <div className="smx-app-shell">
        <header className="smx-topbar">
          <div className="smx-brand-lockup">
            <MontroseBrandMark size={44} />
            <div className="smx-wordmark">
              <strong>MONTROSE</strong>
              <span>TRADING</span>
            </div>
          </div>
          <div className="smx-dashboard-title">Montrose Trading Dashboard</div>
          <div className="smx-top-status"><span className="smx-live-dot"></span> SPORTMONKS LIVE</div>
        </header>

        <main className="smx-main-content">
          <nav className="smx-tabbar" aria-label="Dashboard sections">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={activeTab === tab.id ? "smx-tab smx-tab-active" : "smx-tab"}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          <SportMonksPreviewTab />
        </main>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Montrose Trading Dashboard</h1>

      <div className="flex border-b mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "lineups" && <LineupsTab />}
      {activeTab === "odds" && <OddsTab />}
    </div>
  );
}

export default App;
